import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
} from "@learn-me/ui";
import {
  adminApi,
  type StudentDetailEnhanced,
} from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";
import { Pencil, Loader2 } from "lucide-react";

interface EditStudentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: StudentDetailEnhanced;
}

export function EditStudentModal({
  open,
  onOpenChange,
  student,
}: EditStudentModalProps) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(student.firstName);
  const [lastName, setLastName] = useState(student.lastName);

  useEffect(() => {
    if (open) {
      setFirstName(student.firstName);
      setLastName(student.lastName);
    }
  }, [open, student]);

  const updateMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string }) =>
      adminApi.updateStudent(student.id, data),
    onSuccess: () => {
      toast.success("Datos actualizados exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["admin-student-detail-enhanced", student.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-students-enhanced"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al actualizar los datos");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Editar Estudiante
          </DialogTitle>
          <DialogDescription>
            Actualiza los datos del estudiante.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-firstName">Nombre</Label>
            <Input
              id="edit-firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Nombre"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-lastName">Apellido</Label>
            <Input
              id="edit-lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Apellido"
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                updateMutation.isPending ||
                !firstName.trim() ||
                !lastName.trim()
              }
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar Cambios"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
